/**
 * Small fetch wrapper for the server's /api/* endpoints. Every call sends
 * credentials (the httpOnly session cookie) and applies a timeout, so an
 * unreachable backend — a static-only deployment, a device with no
 * network, the plain `vite preview` server the e2e tests run against —
 * fails fast instead of hanging the UI.
 *
 * ApiError.status is 0 for anything that isn't a real answer from this
 * app's own API (a network failure, a timeout, or a non-JSON response —
 * which in practice means the SPA's own index.html fallback answered
 * instead of a server that was never there). That's the signal callers
 * use to tell "no backend at all" apart from a real HTTP error like 401
 * or 404 — see src/auth/authClient.ts for the most important use of it.
 */
export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function request<T>(path: string, init: RequestInit = {}, timeoutMs = 8000): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  let res: Response;
  try {
    res = await fetch(path, {
      ...init,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
      signal: controller.signal
    });
  } catch {
    throw new ApiError('Could not reach the server.', 0);
  } finally {
    clearTimeout(timeout);
  }

  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    // Not a real API response — almost always means there's no backend
    // reachable at this path (static hosting, or an SPA fallback serving
    // index.html for an unmatched route). Treated the same as a network
    // failure above.
    throw new ApiError('No API available at this path.', 0);
  }

  const body = await res.json().catch(() => ({}) as Record<string, unknown>);
  if (!res.ok) {
    throw new ApiError(typeof body?.error === 'string' ? body.error : `Request failed (HTTP ${res.status}).`, res.status);
  }
  return body as T;
}

export const api = {
  get: <T>(path: string, timeoutMs?: number) => request<T>(path, { method: 'GET' }, timeoutMs),
  post: <T>(path: string, body?: unknown, timeoutMs?: number) =>
    request<T>(path, { method: 'POST', body: body !== undefined ? JSON.stringify(body) : undefined }, timeoutMs),
  del: <T>(path: string, timeoutMs?: number) => request<T>(path, { method: 'DELETE' }, timeoutMs)
};
